import React, { FC, useCallback } from 'react';
import Modal, {
  Appearance,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';
import { translationKeys, useTranslation } from 'services/localization';
import { selectGlobalUnsubscribeDialog } from '../../preferencesSelectors';
import { showGlobalUnsubscribeDialog } from '../../preferencesSlice';
import { trackDeclineUnsubscribeAllClick } from '../../services/userActionEvents';
import { useAppDispatch, useAppSelector } from '../../../../hooks';

interface OwnProps {
  onUnsubscribeClick: () => void;
}

const GlobalUnsubscribeDialog: FC<OwnProps> = ({ onUnsubscribeClick }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation();
  const dialogPayload = useAppSelector(selectGlobalUnsubscribeDialog);

  const acceptHandler = useCallback(async () => {
    onUnsubscribeClick();

    // Close dialog
    dispatch(showGlobalUnsubscribeDialog({ showDialog: false }));
  }, [dispatch, onUnsubscribeClick]);

  const cancelHandler = useCallback(() => {
    trackDeclineUnsubscribeAllClick();

    // Close dialog
    dispatch(showGlobalUnsubscribeDialog({ showDialog: false }));
  }, [dispatch]);

  const actions = [
    {
      text: translate(translationKeys.GLOBAL_UNSUBSCRIBE_DIALOG_ACCEPT_BUTTON_LABEL),
      onClick: acceptHandler,
      autoFocus: true,
      appearance: 'primary' as Appearance,
      testId: 'global-unsubscribe-dialog-accept-button',
    },
    {
      text: translate(translationKeys.GLOBAL_UNSUBSCRIBE_DIALOG_DECLINE_BUTTON_LABEL),
      onClick: cancelHandler,
      appearance: 'subtle' as Appearance,
      testId: 'global-unsubscribe-dialog-cancel-button',
    },
  ];

  return (
    <ModalTransition>
      {dialogPayload.showDialog && (
        <Modal onClose={cancelHandler}>
          <ModalHeader>
            <ModalTitle>{translate(translationKeys.GLOBAL_UNSUBSCRIBE_DIALOG_LABEL)}</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p dangerouslySetInnerHTML={{ __html: translate(translationKeys.GLOBAL_UNSUBSCRIBE_DIALOG_MESSAGE) }} />
          </ModalBody>
          <ModalFooter>
            {actions.map((a) => (
              <Button
                key={a.testId}
                appearance={a.appearance}
                onClick={a.onClick}
                autoFocus={a.autoFocus}
                testId={a.testId}
              >
                {a.text}
              </Button>
            ))}
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};

export default GlobalUnsubscribeDialog;
